import { z } from "zod";
import { useToast } from "../../../../../ui/Toast/ToastContext";
import { useMutation } from "urql";
import {
  Control,
  Controller,
  FormProvider,
  useForm,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useRef, useState } from "react";
import ConfirmationModal from "../../../../../ui/Modal/ConfirmationModal";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../../../../__generated__/gql";
import Label from "../../../../../ui/Label/Label";
import Feedback from "../../../../../ui/Feedback/Feedback";
import Textarea from "../../../../../ui/Textarea/Textarea";
import NumberInput from "../../../../../ui/NumberInput/NumberInput";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { EmbeddedEditableTable } from "../../../../Settings/Generation/components/EditableTable/EditableTable";
import EditableCell from "../../../../Settings/Generation/components/EditableCell/EditableCell";
import OptionsCell from "../../../../Settings/Generation/components/OptionsCell/OptionsCell";
import Badge from "../../../../../ui/Badge/Badge";
import {
  ArticleStatus,
  GenerationStep,
} from "../../../../../__generated__/gql/graphql";
import GeneratingBanner from "./GeneratingBanner";
import Modal from "../../../../../ui/Modal/Modal";
import { AlertTriangle } from "../../../../../ui/Icon/Icon";
import GenerateModal, {
  GenerateArticleModalFormData,
} from "../../../../../components/GenerateModal/GenerateModal";
import classNames from "classnames";
import Button from "../../../../../ui/Button/Button";
import styles from "./ContentBriefForm.module.scss";

const ConfirmChangesModal = ({
  status,
  isOpen,
  onClose,
  onSubmit,
  creditsRequired,
}: {
  status: ArticleStatus;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: GenerateArticleModalFormData) => Promise<void>;
  creditsRequired?: number;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Confirm changes">
      <GenerateModal
        onClose={onClose}
        onSubmit={onSubmit}
        fetching={false}
        confirmationText="Confirm"
        generationOptions={{
          open: true,
          hideBrief: true,
        }}
        defaultValues={{
          regenerate: true,
          regenerateBrief: false,
          regenerateOutline: true,
          regenerateContent: true,
          regenerateLinks: true,
        }}
        articleCounts={{
          total: 1,
          generated:
            status === ArticleStatus.Generated ||
            status === ArticleStatus.Published
              ? 1
              : 0,
          processing: status === ArticleStatus.Processing ? 1 : 0,
          published: status === ArticleStatus.Published ? 1 : 0,
          draft: status === ArticleStatus.Draft ? 1 : 0,
        }}
        creditsRequired={creditsRequired ?? 0}
      >
        <div className={styles.confirmationModal}>
          <AlertTriangle
            size="large"
            className={styles.confirmationModalIcon}
          />
          <div>
            <p>
              Editing this article&apos;s content brief will not automatically
              update the outline. It is recommended to regenerate the outline
              when changes to a content brief are made.
            </p>
          </div>
        </div>
      </GenerateModal>
    </Modal>
  );
};

type Question = {
  id: string;
  question: string;
  answer: string;
};

function Questions({
  control,
  generating,
  regenerating,
}: {
  control: Control<ContentBriefFormData>;
  generating?: boolean;
  regenerating: boolean;
}) {
  const columnHelper = createColumnHelper<Question>();

  const questions = useWatch({ control, name: "questions" });

  const loading = generating && !questions?.length;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<Question, any>[] = [
    columnHelper.accessor("question", {
      header: "Question",
      cell: EditableCell,
    }),
    columnHelper.accessor("answer", {
      header: "Answer",
      cell: EditableCell,
    }),
    ...(!generating
      ? [
          columnHelper.display({
            id: "Options",
            size: 72,
            meta: {
              sticky: true,
            },
            cell: OptionsCell,
          }),
        ]
      : []),
  ];

  if (loading) {
    return (
      <Textarea
        disabled
        loading
        loadingMessage={regenerating ? "Regenerating" : "Generating"}
        rows={10}
      />
    );
  }

  return (
    <EmbeddedEditableTable
      control={control}
      propertyName="questions"
      resourceName={{ singular: "question", plural: "questions" }}
      defaultValue={{ question: "", answer: "" }}
      columns={columns}
      disabled={generating}
    />
  );
}

export const EditArticleBriefFragment = graphql(/* GraphQL */ `
  fragment EditArticleBrief on Article {
    id
    version
    searchIntent
    narrative
    targetWordCount
    processingUntilStep
    status
    sections {
      id
    }
    questions {
      id
      question
      answer
    }
  }
`);

const EditArticleBriefMutation = graphql(/* GraphQL */ `
  mutation editArticleBrief($input: UpdateArticleInput!) {
    updateArticle(input: $input) {
      ...EditArticleBrief
    }
  }
`);

const GenerateFromBriefMutation = graphql(/* GraphQL */ `
  mutation generateFromBrief(
    $id: String!
    $regenerateOutline: Boolean!
    $regenerateContent: Boolean!
  ) {
    generateArticle(
      input: {
        id: $id
        regenerateBrief: false
        regenerateOutline: $regenerateOutline
        regenerateContent: $regenerateContent
      }
    ) {
      ... on Article {
        id
        status
      }
      ... on InsufficientCreditsError {
        __typename
        message
      }
    }
  }
`);

const schema = z.object({
  searchIntent: z.string().optional(),
  narrative: z.string().optional(),
  targetWordCount: z
    .number({
      invalid_type_error: "Required",
    })
    // .min(
    //   1000,
    //   "Invalid word length. Please select a length between 1000 and 3500.",
    // )
    .max(
      3500,
      "Invalid word length. Please select a length between 1000 and 3500.",
    )
    .optional(),
  questions: z
    .array(
      z.object({
        id: z.string().optional(),
        question: z.string().optional(),
        answer: z.string().min(1, "Required"),
      }),
    )
    .optional(),
});

export type ContentBriefFormData = z.infer<typeof schema>;

function ContentBriefForm({
  data,
  className,
  isOpen,
  onClose,
  onChange,
  creditsRequired,
}: {
  data: FragmentType<typeof EditArticleBriefFragment>;
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
  onChange?: (isDirty: boolean) => void;
  creditsRequired?: number;
}) {
  const { showToast } = useToast();

  const article = getFragmentData(EditArticleBriefFragment, data);

  const regenerating = article.version > 1;

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const previousArticleStatus = useRef(article.status);

  const [, editArticleBrief] = useMutation(EditArticleBriefMutation);
  const [, generateFromBriefMutation] = useMutation(GenerateFromBriefMutation);

  const methods = useForm<ContentBriefFormData>({
    defaultValues: {
      searchIntent: article?.searchIntent ?? "",
      narrative: article?.narrative ?? "",
      targetWordCount: article?.targetWordCount ?? undefined,
      questions: article?.questions ?? [],
    },
    resolver: zodResolver(schema),
  });

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    getValues,
    formState: { isSubmitting, isDirty },
  } = methods;

  const generating =
    article.status === ArticleStatus.Processing &&
    (!article.processingUntilStep ||
      article.processingUntilStep === GenerationStep.Outline ||
      article.processingUntilStep === GenerationStep.Content);

  useEffect(() => {
    onChange?.(isDirty);
  }, [isDirty, onChange]);

  useEffect(() => {
    if (previousArticleStatus.current === ArticleStatus.Processing) {
      reset({
        searchIntent: article?.searchIntent ?? "",
        narrative: article?.narrative ?? "",
        targetWordCount: article?.targetWordCount ?? undefined,
        questions: article?.questions ?? [],
      });
    }

    previousArticleStatus.current = article.status;
  }, [article, reset]);

  const onSubmit = useCallback(
    async (options?: GenerateArticleModalFormData) => {
      setIsConfirmationOpen(false);

      const data = getValues();

      const response = await editArticleBrief({
        input: {
          id: article.id,
          searchIntent: data.searchIntent,
          narrative: data.narrative,
          targetWordCount: data.targetWordCount,
          questions: data.questions,
        },
      });

      const responseData = getFragmentData(
        EditArticleBriefFragment,
        response.data?.updateArticle,
      );

      if (options?.regenerateContent || options?.regenerateOutline) {
        const generateArticleResponse = await generateFromBriefMutation({
          id: article.id,
          regenerateContent: options.regenerateContent,
          regenerateOutline: options.regenerateOutline,
        });

        if (
          generateArticleResponse.data?.generateArticle.__typename ===
          "InsufficientCreditsError"
        ) {
          showToast({
            variant: "error",
            title: "You've run out of article credits",
          });
        }
      }

      if (responseData) {
        reset({
          searchIntent: responseData?.searchIntent ?? "",
          narrative: responseData?.narrative ?? "",
          targetWordCount: responseData?.targetWordCount ?? undefined,
          questions: responseData?.questions ?? [],
        });

        showToast({ title: "Article updated" });
      }
    },
    [
      article.id,
      editArticleBrief,
      generateFromBriefMutation,
      getValues,
      reset,
      showToast,
    ],
  );

  return (
    <>
      <FormProvider {...methods}>
        <ConfirmChangesModal
          status={article.status}
          isOpen={isConfirmationOpen}
          onClose={() => setIsConfirmationOpen(false)}
          onSubmit={(data) => handleSubmit(() => onSubmit(data))()}
          creditsRequired={creditsRequired}
        />

        <form
          className={classNames(styles.form, className)}
          onSubmit={handleSubmit(() =>
            article.status === ArticleStatus.Draft && !article.sections?.length
              ? onSubmit()
              : setIsConfirmationOpen(true),
          )}
        >
          {onClose ? (
            <ConfirmationModal
              sticky
              className={styles.formFooter}
              loading={isSubmitting || !isOpen}
              confirmDisabled={!isDirty || generating}
              onClose={onClose}
              confirmationText="Save"
            >
              {generating && <GeneratingBanner id={article.id} />}
              <Label
                label="Keyword search intent"
                tooltip="The intent of the reader when searching for the article's selected keyword. This is based on the highest ranking pages currently for the keyword. Changing the search intent will influence the direction of the article."
              >
                <Controller
                  control={control}
                  name="searchIntent"
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <Textarea
                        {...field}
                        autosize
                        loading={generating && !field.value}
                        loadingMessage={
                          regenerating ? "Regenerating" : "Generating"
                        }
                        minRows={3}
                        disabled={generating}
                      />
                      {error?.message && <Feedback>{error.message}</Feedback>}
                    </div>
                  )}
                />
              </Label>
              <Label
                label="Article narrative"
                tooltip={
                  article.sections?.length
                    ? "An overview of the narrative Macaw should follow when writing the article. Not to be confused with the outline, the narrative acts to guide the outline creation with a overarching story of key beats."
                    : "An overview of the narrative Macaw should follow when writing the article."
                }
              >
                <Controller
                  control={control}
                  name="narrative"
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <Textarea
                        {...field}
                        loading={generating && !field.value}
                        loadingMessage={
                          regenerating ? "Regenerating" : "Generating"
                        }
                        autosize
                        minRows={3}
                        disabled={generating}
                      />
                      {error?.message && (
                        <Feedback className={styles.feedback}>
                          {error.message}
                        </Feedback>
                      )}
                    </div>
                  )}
                />
              </Label>
              <Label
                label="Target word length"
                tooltip="This target is based on the top ranking pages for this article's chosen keyword. Macaw will attempt to reach the target."
              >
                <Controller
                  control={control}
                  name="targetWordCount"
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <NumberInput
                        {...field}
                        loading={generating && !field.value}
                        loadingMessage={
                          regenerating ? "Regenerating" : "Generating"
                        }
                        disabled={generating}
                        value={field.value?.toString()}
                        onChange={async (e) => {
                          field.onChange(parseInt(e.target.value));

                          if (e.target.value) {
                            await trigger("targetWordCount");
                          }
                        }}
                        rightSection={
                          !!field.value &&
                          !error?.message && (
                            <div className={styles.targetWordInfo}>
                              <Badge variant="badge" showDot={false}>
                                {field.value - 250} - {field.value + 250} word
                                target
                              </Badge>
                              <Badge variant="badge" showDot={false}>
                                {Math.min(
                                  Math.max(Math.round(field.value / 250), 4),
                                  14,
                                )}{" "}
                                sections
                              </Badge>
                            </div>
                          )
                        }
                      />
                      {error?.message && (
                        <Feedback className={styles.feedback}>
                          {error.message}
                        </Feedback>
                      )}
                    </div>
                  )}
                />
              </Label>
              <Label
                label="Questions to cover"
                tooltip="An outline of the questions and topics that will be covered in the article, as well as the how Macaw will answer. These are created based on common questions and answers in top ranking pages for this article's keyword."
              >
                <Questions
                  control={control}
                  generating={generating}
                  regenerating={regenerating}
                />
              </Label>
            </ConfirmationModal>
          ) : (
            <>
              {generating && <GeneratingBanner id={article.id} />}
              <Label
                label="Keyword search intent"
                tooltip="The intent of the reader when searching for the article's selected keyword. This is based on the highest ranking pages currently for the keyword. Changing the search intent will influence the direction of the article."
              >
                <Controller
                  control={control}
                  name="searchIntent"
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <Textarea
                        {...field}
                        autosize
                        loading={generating && !field.value}
                        loadingMessage={
                          regenerating ? "Regenerating" : "Generating"
                        }
                        minRows={3}
                        disabled={generating}
                      />
                      {error?.message && <Feedback>{error.message}</Feedback>}
                    </div>
                  )}
                />
              </Label>
              <Label
                label="Article narrative"
                tooltip={
                  article.sections?.length
                    ? "An overview of the narrative Macaw should follow when writing the article. Not to be confused with the outline, the narrative acts to guide the outline creation with a overarching story of key beats."
                    : "An overview of the narrative Macaw should follow when writing the article."
                }
              >
                <Controller
                  control={control}
                  name="narrative"
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <Textarea
                        {...field}
                        loading={generating && !field.value}
                        loadingMessage={
                          regenerating ? "Regenerating" : "Generating"
                        }
                        autosize
                        minRows={3}
                        disabled={generating}
                      />
                      {error?.message && (
                        <Feedback className={styles.feedback}>
                          {error.message}
                        </Feedback>
                      )}
                    </div>
                  )}
                />
              </Label>
              <Label
                label="Target word length"
                tooltip="This target is based on the top ranking pages for this article's chosen keyword. Macaw will attempt to reach the target."
              >
                <Controller
                  control={control}
                  name="targetWordCount"
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <NumberInput
                        {...field}
                        loading={generating && !field.value}
                        loadingMessage={
                          regenerating ? "Regenerating" : "Generating"
                        }
                        disabled={generating}
                        value={field.value?.toString()}
                        onChange={async (e) => {
                          field.onChange(parseInt(e.target.value));

                          if (e.target.value) {
                            await trigger("targetWordCount");
                          }
                        }}
                        rightSection={
                          !!field.value &&
                          !error?.message && (
                            <div className={styles.targetWordInfo}>
                              <Badge variant="badge" showDot={false}>
                                {field.value - 250} - {field.value + 250} word
                                target
                              </Badge>
                              <Badge variant="badge" showDot={false}>
                                {Math.min(
                                  Math.max(Math.round(field.value / 250), 4),
                                  14,
                                )}{" "}
                                sections
                              </Badge>
                            </div>
                          )
                        }
                      />
                      {error?.message && (
                        <Feedback className={styles.feedback}>
                          {error.message}
                        </Feedback>
                      )}
                    </div>
                  )}
                />
              </Label>
              <Label
                label="Questions to cover"
                tooltip="An outline of the questions and topics that will be covered in the article, as well as the how Macaw will answer. These are created based on common questions and answers in top ranking pages for this article's keyword."
              >
                <Questions
                  control={control}
                  generating={generating}
                  regenerating={regenerating}
                />
              </Label>
              <div className={styles.buttonGroup}>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => reset()}
                  disabled={!isDirty || isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  disabled={!isDirty || isSubmitting}
                  loading={isSubmitting}
                  type="submit"
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </form>
      </FormProvider>
    </>
  );
}

export default ContentBriefForm;
